import { Component, DestroyRef, inject, NgZone, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { MaxxSdkService, MaxxSettingsTarget } from '@app/services/maxx-sdk/maxx-sdk.service';
import { ShopService } from '@app/services/shops/shop.service';
import { TracingService } from '@app/services/tracing/tracing.service';
import { first } from 'rxjs';

@Component({
  selector: 'app-maxx',
  templateUrl: './maxx.component.html',
  styleUrls: ['./maxx.component.scss'],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
})
export class MaxxComponent implements OnInit, OnDestroy {
  private maxxChatInstance: MaxxChat | null = null;
  private isEssentials = false;
  private destroyRef = inject(DestroyRef);
  private router = inject(Router);
  private ngZone = inject(NgZone);

  constructor(
    private maxxSdkService: MaxxSdkService,
    private route: ActivatedRoute,
    private shopService: ShopService,
    private tracingService: TracingService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.tracingService.startSessionRecordingForExternalUsers();
    const initialPrompt = this.route.snapshot.queryParams['prompt'] || null;
    const referrer = this.route.snapshot.queryParams['ref'] || 'direct_url';

    this.shopService.shop$.pipe(first(), takeUntilDestroyed(this.destroyRef)).subscribe(async (shop) => {
      this.isEssentials = !!shop?.isEssentials;
      await this.maxxSdkService.loadSdk();
      try {
        this.maxxChatInstance = await this.maxxSdkService.createInstance({
          containerId: 'maxx-chat-container',
          agentName: this.isEssentials ? 'EXTERNAL_ANALYST_AGENT_LEAN_VERSION' : 'EXTERNAL_ANALYST_AGENT',
          isEssentials: this.isEssentials,
          ...(initialPrompt && { initialPrompt }),
          ...(referrer && { referrer }),
          onOpenSettings: (target?: MaxxSettingsTarget) =>
            this.ngZone.run(() => this.router.navigate(['/settings/maxx'], { queryParams: target })),
        });
      } catch (error) {
        console.error('Failed to initialize MaxxChat:', error);
      }
    });
  }

  ngOnDestroy(): void {
    this.maxxSdkService.destroyInstance(this.maxxChatInstance);
    this.maxxChatInstance = null;
  }
}
